import type { QRCodeDomProps } from '@getstep/qrcode-dom'
import type { ValidProfileAlias } from '@getstep/sdk/dist/store/PublicProfileStore'
import { PublicProfileStore } from '@getstep/sdk/dist/store/PublicProfileStore'
import { PublicProfile } from '@getstep/sdk/dist/util/PublicProfile'
import pickBy from 'lodash/pickBy'
import { makeAutoObservable } from 'mobx'

import type { IsomorphicStore } from './IsomorphicStore'
import type { SdkClientStore } from './SdkClientStore'

const QR_CODE_THEMES = {
    blue: [{ color: '#c574fd' }, { color: '#63b6f8' }],
    pink: [{ color: '#f98351' }, { color: '#f969ed' }],
    yellow: [{ color: '#f98e7e' }, { color: '#fcd411' }],
}

type QRCodeTheme = keyof typeof QR_CODE_THEMES

/**
 * Isomorphic store that loads and exposes public user profile.
 */
export class PublicProfilePageStore implements IsomorphicStore<PublicProfileState> {
    /**
     * Current public profile on this page.
     */

    profile?: PublicProfile

    constructor(private readonly sdk: SdkClientStore) {
        makeAutoObservable(this)
    }

    /**
     * Generates a URL to the profile and then returns options for the QR code.
     */
    static generateQRCodeOptions(
        size: number,
        theme: QRCodeTheme,
        url: string,
        restOfProps?: Partial<QRCodeDomProps>,
    ): QRCodeDomProps {
        return {
            ...restOfProps,
            size,
            width: `${size * 2}px`,
            height: `${size * 2}px`,
            value: url,
            backgroundColor: '#363649',
            quietZone: size / 20,
            linearGradient: {
                x1: 0,
                x2: 100,
                y1: 0,
                y2: 200,
                stops: QR_CODE_THEMES[theme],
            },
        }
    }

    /**
     * Loads and saves profile data with the provided alias (username or user ID).
     */

    loadProfile = async (alias: ValidProfileAlias): Promise<void> => {
        const api = await this.sdk.createPublicApi()

        const profile = await PublicProfileStore.findPublicProfile(alias, api)
        if (!profile?.isPublic) return

        this.profile = profile
    }

    /**
     * Resets the store.
     */

    unload = () => {
        this.profile = undefined
    }

    restore({
        displayName,
        status,
        createdAtMs,
        pictureUrl,
    }: PublicProfileState) {
        this.profile = new PublicProfile({
            displayName,
            status,
            createdAtMs,
            pictureUrl,
        })
    }

    toJSON(): PublicProfileState | undefined {
        if (!this.profile) return

        const {
            displayName,
            status,
            createdAtMs,
            pictureUrl,
            username,
            hasPublicPayLink,
        } = this.profile

        return pickBy(
            {
                displayName,
                status,
                createdAtMs,
                pictureUrl,
                username,
                hasPublicPayLink,
            },
            Boolean,
        ) as PublicProfileState
    }
}

export type PublicProfileState = Pick<
    PublicProfile,
    | 'displayName'
    | 'status'
    | 'createdAtMs'
    | 'pictureUrl'
    | 'username'
    | 'hasPublicPayLink'
>
